import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formats, useResponsive } from 'utils';
import { capitalize } from 'utils/formats';

import {
  Card,
  Typography,
  PolicyRow,
  List,
  Button,
  Tabs,
} from '../../../alf-design';
import { LegacyColors } from 'constants';

const getPolicyHolderName = (policy) => {
  if (
    policy?.insured?.firstName === '' &&
    (policy?.insured?.lastName === '' ||
      policy?.insured?.lastName === policy?.organizationName)
  ) {
    return `${capitalize(policy?.organizationName)}`;
  }
  return `${capitalize(policy?.insured?.firstName)} ${capitalize(policy?.insured?.lastName)}`;
};

const hasCoApplicantValue = (value) => {
  const normalizedValue = String(value ?? '').trim();
  return normalizedValue !== '' && normalizedValue.toLowerCase() !== 'na';
};

const PolicyDetails = ({ policy, selectedPolicyFusionNH, toggleDownloads }) => {
  const { showCancellationData, screenIsUp } = useResponsive();
  const [isFusionNHPolicy, setIsFusionNHPolicy] = useState(true);

  const name = getPolicyHolderName(policy);

  let statusStyle = {};
  if (policy.statusCode === 'Cancelled' || policy.statusCode === 'Inactive') {
    statusStyle = { color: LegacyColors.error };
  }

  const PolicyholderInfo = () => {
    return (
      <div className="grid gap-[16px] mb-[16px]">
        <div className="grid grid-cols-2">
          <div className="min-w-0">
            <PolicyRow label="Primary Policyholder" value={name} vertical />
          </div>
          <div className="min-w-0">
            <PolicyRow
              label="Phone"
              value={formats.phoneFormat(policy.insured.phone)}
              vertical
            />
          </div>
        </div>
        <div>
          {policy?.insured?.email && (
            <PolicyRow
              label="Policy Contact Email"
              vertical
              value={policy?.insured?.email}
              tooltip="All policy correspondence will be sent to this email address."
            />
          )}
        </div>
      </div>
    );
  };

  const CoApplicantInfo = () => {
    const firstName = hasCoApplicantValue(policy?.coapplicantFirstName)
      ? policy.coapplicantFirstName
      : '';
    const lastName = hasCoApplicantValue(policy?.coapplicantLastName)
      ? policy.coapplicantLastName
      : '';
    const name =
      !firstName && !lastName
        ? ''
        : `${firstName ? capitalize(firstName) : ''} ${
            lastName ? capitalize(lastName) : ''
          }`;
    const phone = hasCoApplicantValue(policy?.coapplicantPhone)
      ? formats.phoneFormat(policy.coapplicantPhone)
      : '';
    const email = hasCoApplicantValue(policy?.coapplicantEmail)
      ? policy.coapplicantEmail
      : '';

    if (!name) {
      return null;
    }

    return (
      <div className="grid gap-[16px]">
        <div className="grid grid-cols-2">
          <div className="min-w-0">
            <PolicyRow label="Co-Applicant" value={name} vertical />
          </div>
          {phone && (
            <div className="min-w-0">
              <PolicyRow label="Co-Applicant Phone" value={phone} vertical />
            </div>
          )}
        </div>
        {email && (
          <div>
            <PolicyRow label="Co-Applicant Email" vertical value={email} />
          </div>
        )}
      </div>
    );
  };

  const Coverages = ({ policy: coveragesPolicy }) => {
    return (
      <div>
        <List
          title="Coverages"
          data={coveragesPolicy.coverages}
          policySystem={coveragesPolicy.policySystem}
          policyNumber={coveragesPolicy.policyNumber}
        />
        <hr className="border-t border-gray-200 my-[16px]" />
        <List
          title="Deductibles"
          data={coveragesPolicy.deductibles}
          policySystem={coveragesPolicy.policySystem}
          policyNumber={coveragesPolicy.policyNumber}
        />
      </div>
    );
  };

  const FusionPolicySection = ({ policy: fusionPolicy }) => {
    if (!fusionPolicy) {
      return null;
    }

    return (
      <>
        <div className="grid gap-[16px]">
          <div className="grid grid-cols-2">
            <div className="min-w-0">
              <PolicyRow
                label="Policy Number"
                value={fusionPolicy.policyNumber}
              />
            </div>
            <div className="min-w-0">
              <PolicyRow
                label="Effective Date"
                value={`${formats.dateFormat(
                  fusionPolicy.effectiveDate,
                )} to ${formats.dateFormat(fusionPolicy.expirationDate)}`}
              />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="min-w-0">
              <PolicyRow
                label="Status"
                value={fusionPolicy.statusCode}
                valueStyle={
                  fusionPolicy.statusCode === 'Cancelled' ||
                  fusionPolicy.statusCode === 'Inactive'
                    ? { color: LegacyColors.error }
                    : {}
                }
              />
            </div>
            <div className="min-w-0">
              <PolicyRow label="Product" value={fusionPolicy.type} />
            </div>
          </div>
          {showCancellationData && fusionPolicy.statusCode === 'Cancelled' && (
            <div>
              <PolicyRow
                label="Cancellation Reason"
                value={fusionPolicy.cancellationReason}
                vertical
              />
            </div>
          )}
        </div>
        <hr className="border-t border-gray-200 my-[16px]" />
        <Coverages policy={fusionPolicy} />
      </>
    );
  };

  return (
    <div>
      <Card className="mb-[24px]">
        <Typography type="h2" component="h2" className="mb-[24px]">
          Policyholder
        </Typography>
        <PolicyholderInfo />
        <CoApplicantInfo />
      </Card>
      <Card>
        {policy.isFusion ? (
          <>
            <Typography type="h2" component="h2" className="mb-[24px]">
              Slide Fusion Policy Details
            </Typography>
            <Tabs
              id="fusion-policy-tabs"
              tabs={[
                { id: 'nh', label: 'Homeowners Surplus' },
                { id: 'h', label: 'Hurricane Only' },
              ]}
              activeTabId={isFusionNHPolicy ? 'nh' : 'h'}
              onChange={(tabId) => setIsFusionNHPolicy(tabId === 'nh')}
              ariaLabel="Slide Fusion Policy Tabs"
            />
            <hr className="border-t border-legacy-primary mb-[16px]" />
            {isFusionNHPolicy ? (
              <FusionPolicySection policy={selectedPolicyFusionNH} />
            ) : (
              <FusionPolicySection policy={policy} />
            )}
          </>
        ) : (
          <>
            <Typography type="h2" component="h2" className="mb-[24px]">
              Policy Details
            </Typography>
            <div className="grid gap-[16px]">
              <div className="grid grid-cols-2">
                <div className="min-w-0">
                  <PolicyRow
                    label="Policy Number"
                    value={policy.policyNumber}
                  />
                </div>
                <div className="min-w-0">
                  <PolicyRow
                    label="Effective Date"
                    value={`${formats.dateFormat(
                      policy.effectiveDate,
                    )} to ${formats.dateFormat(policy.expirationDate)}`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2">
                <div className="min-w-0">
                  <PolicyRow
                    label="Status"
                    value={policy.statusCode}
                    valueStyle={statusStyle}
                  />
                </div>
                <div className="min-w-0">
                  {policy.price > 0 && (
                    <PolicyRow
                      label="Premium"
                      value={formats.moneyFormat(policy.price)}
                      style={{ marginTop: !screenIsUp('sm') ? 20 : 0 }}
                      vertical
                    />
                  )}
                </div>
              </div>
              {showCancellationData && policy.statusCode === 'Cancelled' && (
                <div>
                  <PolicyRow
                    label="Cancellation Reason"
                    value={policy.cancellationReason}
                    vertical
                  />
                </div>
              )}
              <div className="grid grid-cols-2">
                <div className="min-w-0">
                  <PolicyRow label="Product" value={policy.type} />
                </div>
                <div className="min-w-0">
                  <PolicyRow
                    label="Underwriting Company"
                    value={
                      policy?.policySystem === 'IPX'
                        ? 'Slide Insurance Company'
                        : policy?.company
                    }
                  />
                </div>
              </div>
            </div>
            <hr className="border-t border-gray-200 my-[16px]" />
            <Coverages policy={policy} />
          </>
        )}
        <div className="w-full mt-[32px]">
          <Button
            id="download-policy"
            data-dd-action-name="dashboard.policy_documents.open"
            onClick={() => toggleDownloads(true)}
            className="w-full"
            type="secondary"
          >
            Policy Documents
          </Button>
        </div>
      </Card>
    </div>
  );
};

PolicyDetails.propTypes = {
  toggleDownloads: PropTypes.func,
  policy: PropTypes.object,
  selectedPolicyFusionNH: PropTypes.object,
};

export default PolicyDetails;
